import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import HighchartsReact from 'highcharts-react-official';
import { Common } from '../../utils/common';

const Highcharts = window.Highcharts;

function ProspectBreakdownChart({ chartData, isAutomotiveIndustry, industryType }) {
  const chartComponent = useRef();
  useEffect(() => {
    // This effect reflows the chart to its container whenever isAutomotiveIndustry
    // changes because this value determines whether or not the parent layout is 2 or
    // 3 columns. Without this effect, the container size will change when isAuotomotiveIndustry
    // changes but the chartComponent size will stay the size of the previous container.
    const chart = chartComponent.current.chart;
    chart.reflow();
  }, [isAutomotiveIndustry]);

  let callStatuses = [];

  switch (industryType) {
    case 'Automotive':
      callStatuses = ['Connected Prospects', 'Voicemail', 'Hangups', 'Wrong Department'];
      break;
    case 'Call Center':
      callStatuses = ['Appointment Set', 'No Appointment Set', 'Escalated Service Question', 'Resolved Service Question'];
      break;
    case 'Pest':
      callStatuses = ['New Leads', 'Existing Leads', 'Referral Leads', 'Left Message'];
      break;
    case 'Home Improvement':
      callStatuses = [
        'Answering Service',
        'Disconnected In Progress',
        'Existing Customer Prospect',
        'Left Message',
        'New Prospect',
        'Referral Prospect',
        'Repeat Caller',
      ];
      break;
    case 'Healthcare':
      callStatuses = [
        'Answering Service',
        'Disconnected In Progress',
        'Existing Customer Prospect',
        'Left Message',
        'Prospect',
        'Referral Prospect',
        'Repeat Caller',
        'Current Patient',
      ];
      break;
    case 'Other':
      callStatuses = ['Left Message', 'New Prospect'];
      break;
    default:
      callStatuses = [];
  }

  const formattedChartData = formatChartData(chartData, industryType);

  const chartOptions = {
    chart: {
      height: 300,
      type: 'bar',
    },
    title: {
      text: '',
    },
    subtitle: {
      text: '',
    },
    xAxis: {
      categories: callStatuses,
      labels: {
        rotation: -0,
        style: {
          fontSize: '13px',
          fontFamily: 'Verdana, sans-serif',
        },
      },
    },
    yAxis: {
      min: 0,
      visible: false,
      title: {
        text: '',
      },
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'top',
      layout: 'vertical',
      x: 0,
      y: 100,
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y:.0f}</b>',
    },
    plotOptions: {
      series: {
        colors: ['#1F6587', '#3191C2', '#77C3E1', '#C7E3F1'],
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              if (this.category == 'Connected Prospects') {
                if (Common.in_crm_mode() || window.IN_SCHEDULED_REPORT_MODE) {
                  return;
                }
                location.href =
                  '//' + location.host + '/filtered_call_details/all/prospects/all/all/all';
              } else if (this.category == 'Voicemail') {
                if (Common.in_crm_mode() || window.IN_SCHEDULED_REPORT_MODE) {
                  return;
                }
                location.href =
                  '//' + location.host + '/filtered_call_details/all/all/all/all/voicemail';
              } else if (this.category == 'Hangups') {
                if (Common.in_crm_mode() || window.IN_SCHEDULED_REPORT_MODE) {
                  return;
                }
                location.href =
                  '//' + location.host + '/filtered_call_details/all/all/all/all/hangups';
              } else if (this.category == 'Wrong Department') {
                if (Common.in_crm_mode() || window.IN_SCHEDULED_REPORT_MODE) {
                  return;
                }
                location.href =
                  '//' + location.host + '/filtered_call_details/all/all/all/all/wrongdepartment';
              }
            },
          },
        },
        dataLabels: {
          enabled: true,
          format: '{point.y:.0f}',
          style: {
            color: (Highcharts?.theme && Highcharts?.theme.contrastTextColor) || 'black',
          },
        },
      },
    },
    exporting: { enabled: false },
    credits: { enabled: false },
    series: formattedChartData,
  };

  return (
    <div className='report_data_container'>
      <div className='title'>
        {isAutomotiveIndustry ? 'Prospect Connection' : 'Lead Connection'}
      </div>
      <HighchartsReact ref={chartComponent} highcharts={Highcharts} options={chartOptions} />
    </div>
  );
}

ProspectBreakdownChart.propTypes = {
  chartData: PropTypes.array.isRequired,
  isAutomotiveIndustry: PropTypes.bool.isRequired,
  industryType: PropTypes.string.isRequired,
};

function formatChartData(chartData, industryType) {
  switch (industryType) {
    case 'Automotive':
      return chartData.map((data) => ({
        name: data.calltypetext,
        data: [data.connectedprospects, data.voicemail, data.hangups, data.wrongdepartment],
      }));
    case 'Call Center':
      return chartData.map((data) => ({
        name: data.calltypetext,
        data: [data.appointmentset, data.noappointment, data.escalatedservicequestion, data.resolvedservicequestion],
      }));
    case 'Pest':
      return chartData.map((data) => ({
        name: data.calltypetext,
        data: [
          data.new_prospect,
          data.existing_prospect,
          data.referral_prospect,
          data.left_message,
        ],
      }));
    case 'Home Improvement':
      return chartData.map((data) => ({
        name: data.calltypetext,
        data: [
          data.answering_service,
          data.disconnected_in_progress,
          data.existing_prospect,
          data.left_message,
          data.new_prospect,
          data.referral_prospect,
          data.repeat_caller,
        ],
      }));
    case 'Healthcare':
      return chartData.map((data) => ({
        name: data.calltypetext,
        data: [
          data.answering_service,
          data.disconnected_in_progress,
          data.existing_prospect,
          data.left_message,
          data.prospect,
          data.referral_prospect,
          data.repeat_caller,
          data.current_patient,
        ],
      }));
    case 'Other':
      return chartData.map((data) => ({
        name: data.calltypetext,
        data: [data.left_message, data.new_prospect],
      }));
    default:
      return [];
  }
}

export default ProspectBreakdownChart;
